// Wires up the category tabs in sections/learning-hub.liquid to filter the
// card grid below by matching each card's data-card-category to the active
// tab's data-tab label (case-insensitive, trimmed). Each tab carries its own
// Load style (data-load-style: none/button/scroll/pagination) and Cards per
// page (data-cards-per-page) and Columns desktop (data-columns-desktop), so
// switching tabs reconfigures batching/columns to match that tab's settings.
// Idempotent so re-renders from the theme editor's Section Rendering API
// don't double-bind listeners.
function initLearningHubs(root) {
  root.querySelectorAll('[data-learning-hub]').forEach((hub) => {
    const tabsWrap = hub.querySelector('[data-learning-hub-tabs]');
    const cardsWrap = hub.querySelector('[data-learning-hub-cards]');
    if (!tabsWrap || !cardsWrap || tabsWrap.dataset.learningHubReady) return;
    tabsWrap.dataset.learningHubReady = 'true';

    const tabs = Array.from(tabsWrap.querySelectorAll('[data-tab]'));
    const cards = Array.from(cardsWrap.querySelectorAll('[data-card-category]'));
    const headingRows = Array.from(hub.querySelectorAll('[data-tab-heading]'));
    const loadMoreWrap = hub.querySelector('[data-load-more-wrap]');
    const loadMoreButton = hub.querySelector('[data-load-more]');
    const sentinel = hub.querySelector('[data-load-sentinel]');
    const paginationWrap = hub.querySelector('[data-pagination]');
    if (!tabs.length) return;

    let activeLabel = '';
    let loadStyle = 'none';
    let perPage = 0;
    let revealCount = Infinity; // cumulative reveal (button/scroll)
    let currentPage = 1; // fixed window (pagination)
    let scrollObserver = null;

    function normalize(value) {
      return (value || '').trim().toLowerCase();
    }

    function matchingCards() {
      return cards.filter((card) => normalize(card.dataset.cardCategory) === activeLabel);
    }

    function renderPagination(totalPages) {
      if (!paginationWrap) return;
      paginationWrap.hidden = totalPages <= 1;
      paginationWrap.innerHTML = '';
      for (let page = 1; page <= totalPages; page += 1) {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = String(page);
        if (page === currentPage) button.classList.add('is-active');
        button.addEventListener('click', () => {
          currentPage = page;
          render();
        });
        paginationWrap.appendChild(button);
      }
    }

    function render() {
      const matches = matchingCards();
      cards.forEach((card) => {
        card.hidden = normalize(card.dataset.cardCategory) !== activeLabel;
      });

      if (loadStyle === 'pagination' && perPage) {
        const totalPages = Math.max(1, Math.ceil(matches.length / perPage));
        if (currentPage > totalPages) currentPage = totalPages;
        const start = (currentPage - 1) * perPage;
        const end = start + perPage;
        matches.forEach((card, index) => {
          if (index < start || index >= end) card.hidden = true;
        });
        renderPagination(totalPages);
        return;
      }

      matches.forEach((card, index) => {
        if (index >= revealCount) card.hidden = true;
      });
      const hasMore = perPage > 0 && matches.length > revealCount;
      if (loadMoreWrap) loadMoreWrap.hidden = !(loadStyle === 'button' && hasMore);
      if (sentinel) sentinel.hidden = !(loadStyle === 'scroll' && hasMore);
    }

    function teardownScrollObserver() {
      if (scrollObserver) {
        scrollObserver.disconnect();
        scrollObserver = null;
      }
    }

    function setupScrollObserver() {
      teardownScrollObserver();
      if (!sentinel || loadStyle !== 'scroll' || !perPage) return;
      scrollObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            revealCount += perPage;
            render();
          }
        });
      });
      scrollObserver.observe(sentinel);
    }

    function applyFilter(tab) {
      activeLabel = normalize(tab.dataset.tab);
      loadStyle = tab.dataset.loadStyle || 'none';
      perPage = loadStyle === 'none' ? 0 : parseInt(tab.dataset.cardsPerPage, 10) || 0;
      revealCount = perPage || Infinity;
      currentPage = 1;
      if (paginationWrap && loadStyle !== 'pagination') {
        paginationWrap.hidden = true;
        paginationWrap.innerHTML = '';
      }
      render();
      setupScrollObserver();
      headingRows.forEach((row) => {
        row.hidden = normalize(row.dataset.tabHeading) !== activeLabel;
      });
      if (tab.dataset.columnsDesktop) {
        cardsWrap.style.setProperty('--columns-desktop', tab.dataset.columnsDesktop);
      }
    }

    function activateTab(tab) {
      tabs.forEach((item) => {
        const isActive = item === tab;
        item.classList.toggle('is-active', isActive);
        item.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
      applyFilter(tab);
    }

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => activateTab(tab));
    });

    if (loadMoreButton) {
      loadMoreButton.addEventListener('click', () => {
        revealCount += perPage;
        render();
      });
    }

    activateTab(tabs[0]);
  });
}

document.addEventListener('DOMContentLoaded', () => initLearningHubs(document));
document.addEventListener('shopify:section:load', (event) => initLearningHubs(event.target));
